import React, { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import PageHeader from "../../componentes/PageHeader";
import GradientBanner from "../../componentes/Banner";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFilePdf,
  faFileAlt,
  faDownload,
  faCalendarAlt,
  faBalanceScale,
  faLandmark,
  faFileContract,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import { FullscreenSpinner } from "../../componentes/LoadingIndicator";
import {
  fetchAllRegulations,
  fetchCategories,
  fetchRegulationsByCategory,
} from "../../api/regulations";
import "../../styles/circuit-background.css";

const RegulationCard = ({ regulation, index }) => {
  const { t } = useTranslation();

  const formatFileSize = (sizeStr) => {
    if (typeof sizeStr === "string" && sizeStr.includes("MB")) {
      return sizeStr;
    }
    return sizeStr || "2.5 MB";
  };

  const getFileType = () => {
    if (regulation.file_url) {
      const extension = regulation.file_url.split(".").pop()?.toLowerCase();
      switch (extension) {
        case "pdf":
          return { icon: faFilePdf, label: "PDF", color: "text-red-600", bg: "bg-red-50" };
        case "doc":
        case "docx":
          return { icon: faFileAlt, label: "DOC", color: "text-blue-600", bg: "bg-blue-50" };
        default:
          return {
            icon: faFileAlt,
            label: extension ? extension.toUpperCase() : "DOC",
            color: "text-gray-600",
            bg: "bg-gray-50",
          };
      }
    }
    return { icon: faFilePdf, label: "PDF", color: "text-red-600", bg: "bg-red-50" };
  };

  const getRegulationIcon = (type) => {
    switch (type.name?.toLowerCase()) {
      case "directive":
        return faFileContract;
      case "proclamation":
        return faLandmark;
      case "policy":
        return faShieldAlt;
      case "standard":
        return faBalanceScale;
      default:
        return faBalanceScale;
    }
  };

  const stripHtml = (html) => {
    if (!html) return "";
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const fileType = getFileType();
  const regulationIcon = getRegulationIcon(regulation.regulation_type);
  const cleanDescription = stripHtml(regulation.description);

  return (
    <motion.div
      key={regulation.id || index}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.05, duration: 0.3 }}
      className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300"
    >
      <div className="flex flex-col md:flex-row">
        {/* Image Section - Enhanced Visibility */}
        <div className="md:w-2/5 relative overflow-hidden bg-gray-900">
          {regulation.cover_image_url ? (
            <>
              <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-10"></div>
              <img
                src={regulation.cover_image_url}
                alt={regulation.title}
                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                style={{ minHeight: "280px", maxHeight: "100%" }}
                loading="lazy"
              />
              {/* Image Overlay for better text contrast if needed */}
              <div className="absolute inset-0 bg-black/10 group-hover:bg-black/20 transition-colors duration-300"></div>
            </>
          ) : (
            <div className="w-full h-full min-h-[280px] flex flex-col items-center justify-center bg-gradient-to-br from-primary/10 to-primary/5">
              <div className="w-20 h-20 rounded-full bg-white shadow-lg flex items-center justify-center mb-4">
                <FontAwesomeIcon icon={regulationIcon} className="text-3xl text-primary" />
              </div>
              <span className="text-lg font-semibold text-gray-700">{regulation.year}</span>
              <span className="text-sm text-gray-500 mt-1 px-3 py-1 bg-white/80 rounded-full">
                {regulation.regulation_type.name}
              </span>
            </div>
          )}

        </div>

        {/* Content Section */}
        <div className="flex-1 p-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-lg ${fileType.bg}`}>
                <FontAwesomeIcon icon={fileType.icon} className={`text-xs ${fileType.color}`} />
                <span className={`text-xs font-medium ${fileType.color}`}>{fileType.label}</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-gray-500 bg-gray-50 px-2.5 py-1 rounded-lg">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-xs" />
              <span className="font-medium">{regulation.year}</span>
            </div>
          </div>

          {/* Title */}
          <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight line-clamp-2">
            {regulation.title}
          </h3>

          {/* Description */}
          {cleanDescription && (
            <p className="text-sm text-gray-600 mb-4 line-clamp-2 leading-relaxed">
              {cleanDescription}
            </p>
          )}

          {/* Type Badge */}
          {regulation.regulation_type.name && (
            <div className="mb-5">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-50 text-sm text-gray-700 border border-gray-100">
                <FontAwesomeIcon icon={regulationIcon} className="text-sm text-primary" />
                <span className="font-medium">{regulation.regulation_type.name}</span>
              </span>
            </div>
          )}

          {/* Download Button */}
          <a
            href={regulation.file_url}
            download
            className="inline-flex items-center justify-between w-full px-5 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg transition-all duration-200 group/btn"
          >
            <span className="flex items-center">
              <FontAwesomeIcon icon={faDownload} className="mr-2 text-sm" />
              Download
            </span>
            <span className="text-xs bg-white/20 px-2 py-0.5 rounded-md">
              {formatFileSize(regulation.file_size)}
            </span>
          </a>
        </div>
      </div>
    </motion.div>
  );
};
const EmptyRegulations = ({ message }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    className="col-span-2 bg-white p-12 rounded-xl shadow-md text-center text-gray-500 border border-dashed border-gray-300"
  >
    <FontAwesomeIcon icon={faFileAlt} className="text-5xl mb-4 text-gray-400" />
    <h3 className="text-xl font-bold mb-2">No regulations available</h3>
    <p>{message}</p>
  </motion.div>
);

const Regulations = () => {
  const { t, i18n } = useTranslation();
  const [activeCategory, setActiveCategory] = useState(null);

  // Fetch all regulations across all pages
  const {
    data: allRegulationsData,
    isLoading: allRegulationsLoading,
    error: allRegulationsError,
  } = useQuery({
    queryKey: ["allRegulations", i18n.language],
    queryFn: () => fetchAllRegulations({ lang: i18n.language }),
  });

  const {
    data: categoriesData,
    isLoading: categoriesLoading,
    error: categoriesError,
  } = useQuery({
    queryKey: ["regulationCategories", i18n.language],
    queryFn: () => fetchCategories(i18n.language),
    onError: (err) => {
      console.warn("Could not fetch from categories endpoint:", err);
    },
  });

  const {
    data: regulationsData,
    isLoading: regulationsLoading,
    error: regulationsError,
  } = useQuery({
    queryKey: ["regulations", activeCategory?.id, i18n.language],
    queryFn: () =>
      fetchRegulationsByCategory({ activeCategory, lang: i18n.language }),
    enabled: !!activeCategory,
  });

  const categories = React.useMemo(() => {
    if (!categoriesData || !allRegulationsData) return [];

    const filteredCategories = categoriesData
      .filter((cat) => {
        const hasRegulations = allRegulationsData.some(
          (reg) => reg.regulation_type?.id === cat.id
        );
        return hasRegulations;
      })
      .map((cat) => ({
        id: cat.id,
        name: cat.name,
      }));

    console.log("Filtered Categories:", filteredCategories);
    return filteredCategories;
  }, [categoriesData, allRegulationsData]);

  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0]);
    }
  }, [categories]);

  const handleCategoryClick = (category) => {
    setActiveCategory(category);
  };

  const getCategoryIcon = (category) => {
    switch (category.name?.toLowerCase()) {
      case "directive":
        return faFileContract;
      case "proclamation":
        return faLandmark;
      case "policy":
        return faShieldAlt;
      case "standard":
        return faBalanceScale;
      default:
        return faFileAlt;
    }
  };

  const renderContent = () => {
    if (categoriesLoading || regulationsLoading || allRegulationsLoading) {
      return <FullscreenSpinner className="py-12" />;
    }

    // Check if no categories or regulations exist instead of showing an error
    if (
      !categories.length &&
      (!allRegulationsData || allRegulationsData.length === 0)
    ) {
      return (
        <div className="bg-white p-10 rounded-xl shadow-md text-center text-gray-500">
          <FontAwesomeIcon
            icon={faBalanceScale}
            className="text-5xl mb-4 text-gray-400"
          />
          <h3 className="text-xl font-semibold">
            {t("regulations.noRegulations")}
          </h3>
          <p>
            {t(
              "regulations.noDataMessage",
              "No regulations have been added yet."
            )}
          </p>
        </div>
      );
    }

    return (
      <>
        <motion.h2
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-h-5 font-bold mb-8 text-gray-800 border-b-2 border-primary pb-2 inline-block"
        >
          {activeCategory?.name || ""}
        </motion.h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 2xl:grid-cols-3 gap-4 md:gap-4">
          {regulationsData?.length > 0 ? (
            regulationsData.map((regulation, index) => (
              <RegulationCard
                key={regulation.id || index}
                regulation={regulation}
                index={index}
              />
            ))
          ) : (
            <EmptyRegulations
              message={t("regulations.no_regulations_in_category", {
                category: activeCategory?.name.toLowerCase() || "",
              })}
            />
          )}
        </div>
      </>
    );
  };
  return (
    <>
      <PageHeader />
      <GradientBanner
        title={t("regulations.title", "Regulations")}
        description={t(
          "regulations.subtitle",
          "Official regulatory documents and standards for certification"
        )}
        path={[
          { label: t("header.home", "Home"), link: "/" },
          { label: t("header.regulations", "Regulations"), link: null },
        ]}
        backgroundIcons={[
          faBalanceScale,
          faFileContract,
          faLandmark,
          faShieldAlt,
        ]}
      />
      <div className="circuit-page-background">
        <div className="py-16 font-nunito-sans">
          <div className="px-dynamic">
          <div className="flex flex-col lg:flex-row gap-8">
            <div className="lg:w-[220px] flex-shrink-0">
              <div className="bg-primary text-white p-4 rounded-t-lg shadow-md">
                <h3 className="text-h-7 font-bold uppercase tracking-wide">
                  {t("regulations.categories", "Categories")}
                </h3>
              </div>
              <div className="bg-white shadow-lg rounded-b-lg overflow-hidden sticky top-24">
                {categories.map((category, idx) => (
                  <motion.button
                    key={category.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.05 }}
                    onClick={() => handleCategoryClick(category)}
                    className={`flex items-center justify-between w-full p-4 text-left border-b border-gray-100 hover:bg-gray-50 transition-all ${
                      activeCategory?.id === category.id
                        ? "font-bold text-primary border-l-4 border-l-primary pl-3"
                        : "border-l-4 border-l-transparent"
                    }`}
                  >
                    <div className="flex items-center">
                      <FontAwesomeIcon
                        icon={getCategoryIcon(category)}
                        className="mr-3 text-primary"
                      />
                      <span className="text-lg">{category.name}</span>{" "}
                      {/* Use raw name */}
                    </div>
                    <span
                      className={`transform transition-transform ${
                        activeCategory?.id === category.id ? "rotate-90" : ""
                      }`}
                    >
                      →
                    </span>
                  </motion.button>
                ))}
              </div>
            </div>
            <div className="flex-grow">{renderContent()}</div>
          </div>
        </div>
        </div>
      </div>
    </>
  );
};

export default Regulations;
